'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import type { ShadcnTabsConfig, ShadcnPageConfig } from '../types';

interface TabsRendererProps {
  tabs: ShadcnTabsConfig;
  apiBaseUrl?: string;
  renderPage: (page: ShadcnPageConfig) => ReactNode;
}

function buildEndpoint(baseUrl: string, endpoint: string): string {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }

  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const normalizedPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${normalizedBase}${normalizedPath}`;
}

function TabLoadingSkeleton() {
  return (
    <div className="space-y-4" aria-live="polite" aria-busy="true">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-80" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

export function TabsRenderer({ tabs, apiBaseUrl = '', renderPage }: TabsRendererProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const initialTab = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabs.items.some((item) => item.id === tabFromUrl)) {
      return tabFromUrl;
    }

    return tabs.defaultTabId ?? tabs.items[0]?.id;
  }, [location.search, tabs.defaultTabId, tabs.items]);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [hydratedTabs, setHydratedTabs] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (!activeTab) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('tab') === activeTab) {
      return;
    }

    searchParams.set('tab', activeTab);
    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    }, { replace: true });
  }, [activeTab, location.pathname, location.search, navigate]);

  const activeTabConfig = tabs.items.find((item) => item.id === activeTab);

  const loadingQuery = useQuery({
    queryKey: ['tabs-layout', activeTabConfig?.id, activeTabConfig?.loadingEndpoint],
    queryFn: async () => {
      const endpoint = activeTabConfig?.loadingEndpoint;
      if (!endpoint) {
        return null;
      }

      const response = await fetch(buildEndpoint(apiBaseUrl, endpoint));
      if (!response.ok) {
        throw new Error(`Failed to load tab data from ${endpoint}`);
      }

      return response.json() as Promise<unknown>;
    },
    enabled: Boolean(activeTabConfig?.loadingEndpoint) && !hydratedTabs[activeTabConfig?.id ?? ''],
  });

  useEffect(() => {
    if (!activeTabConfig?.id || hydratedTabs[activeTabConfig.id]) {
      return;
    }

    if (!activeTabConfig.loadingEndpoint || loadingQuery.status !== 'pending') {
      setHydratedTabs((previous) => ({
        ...previous,
        [activeTabConfig.id]: true,
      }));
    }
  }, [activeTabConfig, hydratedTabs, loadingQuery.status]);

  if (!tabs.items.length || !activeTab) {
    return null;
  }

  const isLoading = Boolean(activeTabConfig?.loadingEndpoint) && !hydratedTabs[activeTab] && loadingQuery.status === 'pending';

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList>
        {tabs.items.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.id}>{tab.label}</TabsTrigger>
        ))}
      </TabsList>

      {tabs.items.map((tab) => (
        <TabsContent key={tab.id} value={tab.id}>
          {isLoading && tab.id === activeTab ? <TabLoadingSkeleton /> : renderPage(tab.pageConfig)}
        </TabsContent>
      ))}
    </Tabs>
  );
}
